
import { useState } from 'react';
import GroupBadgeDisplay from '../../../../components/base/GroupBadgeDisplay';
import GroupLevelDisplay from '../../../../components/base/GroupLevelDisplay';
import GroupModerationTools from './GroupModerationTools';
import RoleManagementDropdown from './RoleManagementDropdown';

interface Member {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
  badge: string | null;
  level: number;
  joined_at: string;
  user_profiles: {
    username: string;
    avatar_url: string;
  };
}

interface MembersTabProps {
  members: Member[];
  currentUserRole: string | null;
  groupId: string;
  onRoleChange?: (userId: string, newRole: string) => void;
  onRemoveMember?: (userId: string) => void;
  onRefresh?: () => void;
}

const MembersTab = ({ members, currentUserRole, groupId, onRoleChange, onRemoveMember, onRefresh }: MembersTabProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [roleChangeLoading, setRoleChangeLoading] = useState<string | null>(null);

  const getRoleIcon = (role: string) => {
    const icons: Record<string, { icon: string; color: string; bg: string; label: string }> = {
      owner: { icon: 'ri-vip-crown-fill', color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'Owner' },
      admin: { icon: 'ri-shield-star-fill', color: 'text-rose-500', bg: 'bg-rose-50', label: 'Admin' },
      moderator: { icon: 'ri-shield-check-fill', color: 'text-teal-500', bg: 'bg-teal-50', label: 'Moderator' },
      member: { icon: 'ri-user-fill', color: 'text-gray-400', bg: 'bg-gray-50', label: 'Member' },
    };
    return icons[role] || icons.member;
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = member.user_profiles.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!onRoleChange) return;
    setRoleChangeLoading(userId);
    try {
      await onRoleChange(userId, newRole);
    } finally {
      setRoleChangeLoading(null);
    }
  };

  const handleActionComplete = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // Role counts
  const roleCounts = {
    all: members.length,
    owner: members.filter(m => m.role === 'owner').length,
    admin: members.filter(m => m.role === 'admin').length,
    moderator: members.filter(m => m.role === 'moderator').length,
    member: members.filter(m => m.role === 'member').length,
  };

  const filterButtons = [
    { key: 'all', label: 'All' },
    { key: 'owner', label: 'Owners' },
    { key: 'admin', label: 'Admins' },
    { key: 'moderator', label: 'Moderators' },
    { key: 'member', label: 'Members' },
  ];

  return (
    <div className="space-y-4">
      {/* Role Summary Bar */}
      {canManageMembers && (
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-team-fill text-teal-500"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Team Overview</h3>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[
              { role: 'owner', count: roleCounts.owner },
              { role: 'admin', count: roleCounts.admin },
              { role: 'moderator', count: roleCounts.moderator },
              { role: 'member', count: roleCounts.member },
            ].map(({ role, count }) => {
              const info = getRoleIcon(role);
              return (
                <div key={role} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${info.bg}`}>
                  <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center ${info.color}`}>
                    <i className={`${info.icon} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900 leading-none">{count}</p>
                    <p className="text-xs text-gray-500">{info.label}{count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {filterButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setRoleFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                roleFilter === key
                  ? 'bg-teal-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
              {key !== 'all' && roleCounts[key as keyof typeof roleCounts] > 0 && (
                <span className={`ml-1.5 text-xs ${roleFilter === key ? 'text-teal-100' : 'text-gray-400'}`}>
                  {roleCounts[key as keyof typeof roleCounts]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
        {filteredMembers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <i className="ri-user-search-line text-4xl mb-2"></i>
            <p>No members found</p>
          </div>
        ) : (
          filteredMembers.map((member) => {
            const roleInfo = getRoleIcon(member.role);
            const badges = member.badge ? member.badge.split(',') : [];
            
            return (
              <div key={member.id} className="p-4 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="relative">
                      <img
                        src={member.user_profiles.avatar_url}
                        alt={member.user_profiles.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center ${roleInfo.color}`}>
                        <i className={`${roleInfo.icon} text-sm`}></i>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{member.user_profiles.username}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.bg} ${roleInfo.color}`}>
                          <i className={`${roleInfo.icon} text-[10px]`}></i>
                          {roleInfo.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <GroupLevelDisplay level={member.level} size="sm" showLabel={false} />
                        <GroupBadgeDisplay badges={badges} size="sm" maxDisplay={3} />
                        <span className="text-xs text-gray-500">
                          Joined {new Date(member.joined_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Area */}
                  {canManageMembers && member.role !== 'owner' && (
                    <div className="flex items-center gap-1">
                      {/* Inline Role Management */}
                      <RoleManagementDropdown
                        memberId={member.user_id}
                        memberName={member.user_profiles.username}
                        currentRole={member.role}
                        currentUserRole={currentUserRole as 'owner' | 'admin' | 'moderator' | 'member'}
                        onRoleChange={handleRoleChange}
                        isLoading={roleChangeLoading === member.user_id}
                      />

                      {/* Moderation Tools (mute, ban, etc.) */}
                      <GroupModerationTools
                        groupId={groupId}
                        memberId={member.user_id}
                        memberRole={member.role}
                        currentUserRole={currentUserRole || 'member'}
                        onActionComplete={handleActionComplete}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MembersTab;
